import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "./api";
import { setToken, clearToken, hasToken } from "./tokenStorage";
import { useNotificationSocket } from "./useNotificationSocket";
import { setDatePref } from "./formatters";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      if (data?.date_format) setDatePref(data.date_format);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasToken()) fetchMe();
    else setLoading(false);
  }, [fetchMe]);

  useEffect(() => {
    const onProfileUpdate = () => { if (hasToken()) fetchMe(); };
    window.addEventListener("aii:profile_updated", onProfileUpdate);
    return () => window.removeEventListener("aii:profile_updated", onProfileUpdate);
  }, [fetchMe]);

  // Real-time notifications + emergency alerts (WS + REST fallback).
  const {
    notifications, setNotifications,
    emergency, setEmergency,
    wsConnected, refreshNotifications,
  } = useNotificationSocket(user);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    await fetchMe();
    return data;
  };

  const logout = () => {
    clearToken();
    setUser(null);
    window.location.href = "/login";
  };

  const impersonate = async (role) => {
    const { data } = await api.post("/auth/impersonate", { role });
    setToken(data.token);
    await fetchMe();
    toast.success(`Viewing as ${role}`);
  };

  const stopImpersonation = async () => {
    const { data } = await api.post("/auth/stop-impersonation");
    setToken(data.token);
    await fetchMe();
    toast.success("Back to Founder view");
  };

  const dismissEmergency = async (id) => {
    await api.post(`/emergency/${id}/dismiss`);
    setEmergency(null);
  };

  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    await refreshNotifications();
    // Optimistic: reflect read state in local list until the next poll.
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <AuthCtx.Provider value={{
      user, loading, login, logout, impersonate, stopImpersonation,
      notifications, refreshNotifications, markAllRead,
      emergency, dismissEmergency, wsConnected,
    }}>
      {children}
    </AuthCtx.Provider>
  );
};

export const useAuth = () => useContext(AuthCtx);
